import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CATEGORIES = [
  { value: "service", label: "Service / vedlikehold" },
  { value: "feil", label: "Feilretting" },
  { value: "inspeksjon", label: "Inspeksjon" },
  { value: "sporsmal", label: "Spørsmål til Proxima" },
  { value: "annet", label: "Annet" },
];

/**
 * "Kontakt Proxima / Bestill oppdrag" dialog. plants: list for selection (optional when plant fixed).
 */
export const ServiceRequestDialog = ({ open, onOpenChange, plant, plants = [], onCreated, defaultCategory = "service" }) => {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ plant_id: plant?.id || "", category: defaultCategory, subject: "", description: "", contact_name: user?.name || "", phone: user?.phone || "", preferred_time: "", priority: "normal" });
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const effectivePlantId = plant?.id || form.plant_id;

  const submit = async (e) => {
    e.preventDefault();
    if (form.subject.trim().length < 3 || form.description.trim().length < 3) {
      toast.error("Fyll inn emne og beskrivelse.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/service/requests", { ...form, plant_id: effectivePlantId || null });
      toast.success(`Henvendelse ${data.ref} er sendt til Proxima`, { description: "Du finner den under Service." });
      onCreated?.(data);
      onOpenChange(false);
      setForm((f) => ({ ...f, subject: "", description: "", preferred_time: "" }));
    } catch (err) {
      toast.error(errorMessage(err, "Kunne ikke sende henvendelsen."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="service-request-dialog">
        <DialogHeader>
          <DialogTitle>Bestill oppdrag / kontakt Proxima</DialogTitle>
          <DialogDescription>Henvendelsen registreres i portalen og følges opp av Proxima service. Ingen styring av anlegget utføres herfra.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="service-request-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Kraftverk</Label>
              {plant ? (
                <Input value={plant.name} disabled data-testid="service-request-plant" />
              ) : (
                <Select value={form.plant_id} onValueChange={set("plant_id")}>
                  <SelectTrigger data-testid="service-request-plant-select">
                    <SelectValue placeholder="Velg kraftverk (valgfritt)" />
                  </SelectTrigger>
                  <SelectContent>
                    {plants.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Type henvendelse</Label>
              <Select value={form.category} onValueChange={set("category")}>
                <SelectTrigger data-testid="service-request-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Prioritet</Label>
              <Select value={form.priority} onValueChange={set("priority")}>
                <SelectTrigger data-testid="service-request-priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lav">Lav</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="hoy">Høy – påvirker produksjon</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="sr-subject">Emne</Label>
              <Input id="sr-subject" value={form.subject} onChange={(e) => set("subject")(e.target.value)} placeholder="F.eks. Rensk av inntaksrist" data-testid="service-request-subject" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="sr-desc">Beskrivelse</Label>
              <Textarea id="sr-desc" value={form.description} onChange={(e) => set("description")(e.target.value)} rows={4} placeholder="Beskriv behovet, observasjoner og eventuelle tidspunkter." data-testid="service-request-description" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sr-contact">Kontaktperson</Label>
              <Input id="sr-contact" value={form.contact_name} onChange={(e) => set("contact_name")(e.target.value)} data-testid="service-request-contact" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sr-phone">Telefon</Label>
              <Input id="sr-phone" value={form.phone} onChange={(e) => set("phone")(e.target.value)} data-testid="service-request-phone" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="sr-time">Ønsket tidspunkt (valgfritt)</Label>
              <Input id="sr-time" value={form.preferred_time} onChange={(e) => set("preferred_time")(e.target.value)} placeholder="F.eks. uke 44, formiddag" data-testid="service-request-time" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid="service-request-cancel">
              Avbryt
            </Button>
            <Button type="submit" disabled={busy} data-testid="kraftverk-kontakt-submit">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              Send henvendelse
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
